import React from 'react';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, Stack, Typography } from '@mui/material';

import { describeFilter, isEmptyFilterValue } from './filterHelpers';
import { useFilterMenuTokens } from './filterMenuTokens';
import type { FilterDescription, FilterFieldDef, FiltersState, FilterValue } from './types';

const MAX_VALUE_ICONS = 3;

/** Icons of the selected options, shown clustered in the chip's value segment. */
const getValueIcons = (def: FilterFieldDef, value: FilterValue): React.ReactNode[] => {
  if (def.type !== 'multiSelect' || value.kind !== 'multiSelect') return [];
  const iconByValue = new Map(def.options.map(option => [option.value, option.icon]));
  return value.values.map(selected => iconByValue.get(selected)).filter(icon => icon !== undefined);
};

export interface AppliedFilterChipsProps {
  fields: FilterFieldDef[];
  value: FiltersState;
  /** Chip body clicked — reopen the field's editor anchored to the chip. */
  onEdit: (fieldId: string, anchorEl: HTMLElement) => void;
  onRemove: (fieldId: string) => void;
  /** Renders the trailing "+" button when provided. */
  onAdd?: (anchorEl: HTMLElement) => void;
}

interface FilterChipProps {
  def: FilterFieldDef;
  description: FilterDescription;
  /** Selected options' icons, rendered as an overlapped cluster before the value text. */
  valueIcons: React.ReactNode[];
  /** Absent for switch-display booleans — there is no editor to reopen. */
  onEdit?: (anchorEl: HTMLElement) => void;
  onRemove: () => void;
}

const FilterChip: React.FC<FilterChipProps> = ({
  def,
  description,
  valueIcons,
  onEdit,
  onRemove,
}) => {
  const tokens = useFilterMenuTokens();
  const editable = onEdit !== undefined;

  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'stretch',
        border: `1px solid ${tokens.line}`,
        borderRadius: `${tokens.radius}px`,
        height: 28,
        overflow: 'hidden',
      }}
    >
      <Stack
        component={editable ? 'button' : 'div'}
        type={editable ? 'button' : undefined}
        direction="row"
        onClick={
          editable
            ? (event: React.MouseEvent<HTMLElement>) => onEdit(event.currentTarget)
            : undefined
        }
        aria-label={editable ? `Edit ${description.field} filter` : undefined}
        sx={{
          alignItems: 'center',
          bgcolor: 'transparent',
          border: 'none',
          cursor: editable ? 'pointer' : 'default',
          gap: 0.75,
          px: 1,
          ...(editable && {
            '&:hover, &:focus-visible': { bgcolor: tokens.hoverBg },
          }),
        }}
      >
        {def.icon && (
          <Box
            sx={{
              alignItems: 'center',
              color: tokens.dim,
              display: 'flex',
              fontSize: 14,
            }}
          >
            {def.icon}
          </Box>
        )}
        <Typography sx={{ color: tokens.ink, fontSize: tokens.metaFontSize }}>
          {description.field}
        </Typography>
        <Typography sx={{ color: tokens.dim, fontSize: tokens.metaFontSize }}>
          {description.op}
        </Typography>
        {valueIcons.length > 0 && (
          <Stack aria-hidden direction="row" sx={{ alignItems: 'center' }}>
            {valueIcons.slice(0, MAX_VALUE_ICONS).map((icon, index) => (
              <Box
                // Icon order mirrors the selection order; index is the identity.
                key={index}
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  ml: index === 0 ? 0 : -0.5,
                }}
              >
                {icon}
              </Box>
            ))}
          </Stack>
        )}
        <Typography noWrap sx={{ color: tokens.ink, fontSize: tokens.metaFontSize }}>
          {description.value}
        </Typography>
      </Stack>
      <IconButton
        size="small"
        aria-label={`Remove ${description.field} filter`}
        onClick={onRemove}
        sx={{
          borderLeft: `1px solid ${tokens.line}`,
          borderRadius: 0,
          color: tokens.dim,
          px: 0.5,
        }}
      >
        <CloseIcon sx={{ fontSize: 13 }} />
      </IconButton>
    </Stack>
  );
};

/**
 * Removable, re-editable chips for the applied filters, rendered above the
 * table. Renders nothing while no filter is applied — the host's main
 * trigger covers the empty state.
 */
export const AppliedFilterChips: React.FC<AppliedFilterChipsProps> = ({
  fields,
  value,
  onEdit,
  onRemove,
  onAdd,
}) => {
  const tokens = useFilterMenuTokens();
  const applied = fields.filter(field => {
    const fieldValue = value[field.id];
    return fieldValue !== undefined && !isEmptyFilterValue(fieldValue);
  });

  if (applied.length === 0) return null;

  return (
    <Stack direction="row" sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
      {applied.map(def => {
        const isSwitch = def.type === 'boolean' && def.display === 'switch';
        return (
          <FilterChip
            key={def.id}
            def={def}
            description={describeFilter(def, value[def.id])}
            valueIcons={getValueIcons(def, value[def.id])}
            onEdit={isSwitch ? undefined : anchorEl => onEdit(def.id, anchorEl)}
            onRemove={() => onRemove(def.id)}
          />
        );
      })}
      {onAdd && (
        <IconButton
          size="small"
          aria-label="Add filter"
          onClick={event => onAdd(event.currentTarget)}
          sx={{ color: tokens.dim }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}
    </Stack>
  );
};
